import {Injectable} from '@angular/core';
import {ActivatedRoute, Params} from "@angular/router";
import {FilterControl} from "../interface/filter-control";
import {FilterControls} from "../interface/filter-controls";
import {Observable, Subscription} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {FilterOption} from "../interface/filter-option";
import {FilterCsvMetadataService} from "./filter-csv-metadata.service";
import {ProductMetadataInfo} from "../interface/product-metadata-info";

@UntilDestroy()
@Injectable({
	providedIn: 'root'
})
export class FilterUrlDecoderService {

	constructor(
		private _activatedRoute: ActivatedRoute
	) {
	}

	private _prepareFilterControls(
		decodedParam: string,
		control: FilterControl,
		filterKey: string
	): void {
		switch (control.key.type) {
			case "toggle":
				if(!control.cohort)
					return;
				if(filterKey === "inStock") {
					control.cohort.options[0].active = !!decodedParam;
				}
				break;
			case "default":
				this._defaultFilterControls(decodedParam, control);
				break;
			case "csv":
				this._csvOptionFilterControls(decodedParam, control);
				break;
			case "range":
				this._rangeOptionFilterControls(decodedParam, control, filterKey);
				break;
		}
	}

	private _prepareSubOptionFilterControls(
		control: FilterControl,
		queryParam: Params
	): void {
		if (!control.cohort)
			return;
		control.cohort.options.forEach((option: FilterOption): void => {
			if(!option.subOptions)
				return;
			const filterKey: string = option.value.toLowerCase().replace(/\s+/g, '-');
			const parameter: string | undefined = queryParam[encodeURIComponent(filterKey)];
			if (parameter === undefined)
				return;

			const decodedParam: string = decodeURIComponent(parameter);
			if(decodedParam === "all") {
				option.subOptions.forEach(subOption => subOption.active = true);
				option.active = true;
			}
			else {
				this._subOptionsFilterControls(decodedParam, option.subOptions);
				option.active = option.subOptions.every(subOption => subOption.active);
			}
		})
	}

	private _defaultFilterControls(
		decodedParam: string,
		control: FilterControl
	): void {
		const filterValues: string[] = decodedParam.split(",").map((filter) => filter.toLowerCase().replace(/-/g, ' '));
		if (!control.cohort)
			return;
		control.cohort.options.forEach(controlOption => {
			if (filterValues.indexOf(controlOption.value.toLowerCase()) >= 0)
				controlOption.active = true;
		});
	}

	private _subOptionsFilterControls(
		decodedParam: string,
		control: FilterOption[]
	): void {
		const filterValues: string[] = decodedParam.split(",")
			.map((filter) => filter.toLowerCase().replace(/-/g, ' '));

		control.forEach((option: FilterOption): void => {
			const subOptionKey: string = option.value.toLowerCase();
			if(subOptionKey.includes('-')) {
				if (
					filterValues.indexOf(
						subOptionKey
							.replace(/\s+/g, '')
							.replace(/-/g,' ')
					) >= 0) {
					option.active = true;
				}
			}
			else {
				if (filterValues.indexOf(subOptionKey) >= 0) {
					option.active = true;
				}
			}
		})
	}

	private _csvOptionFilterControls(
		decodedParam: string,
		control: FilterControl,
	): void {
		const filterValues: string[] = decodedParam.split(",")
			.map((filter) => filter.toLowerCase().replace(/-/g, ' '));
		if (!control.cohort)
			return;
		let metadataList: ProductMetadataInfo[] = [];
		switch (control.key.key) {
			case "color":
				metadataList = FilterCsvMetadataService.colorList;
				break;
			case "material":
				metadataList = FilterCsvMetadataService.materialList;
				break;
			case "pattern":
				metadataList = FilterCsvMetadataService.patternList;
				break;
		}

		control.cohort.options.forEach(controlOption => {
			const controlOptionId = metadataList
				.find(metadata => metadata.id.toString() === controlOption.value);
			if(!controlOptionId)
				return;

			if (filterValues.indexOf(controlOptionId.name.toLowerCase()) >= 0)
					controlOption.active = true;
		});
	}

	private _rangeOptionFilterControls(
		decodedParam: string,
		control: FilterControl,
		filterKey: string
	): void {
		const filterValues: string[] = decodedParam.split(",").map(filter => filter.toLowerCase());

		if (!control.rangeCohort)
			return;

		if (control.key.key === filterKey) {
			let minValue = filterValues[0].replace('min-', '');
			let maxValue = filterValues[1].replace('max-', '');
			control.rangeCohort.active = true;
			control.rangeCohort.value1 = +minValue;
			control.rangeCohort.value2 = +maxValue;
		}
	}

	public decode(filterControls: FilterControls): void {

		const queryParams: Observable<Params> = this._activatedRoute.queryParams;

		const subscription: Subscription = queryParams
			.pipe(untilDestroyed(this))
			.subscribe(queryParam => {

				filterControls.cohorts.forEach(cohort => {

					if(cohort.key.type !== "sub") {
						const filterKey: string = cohort.key.key;
						const parameter: string | undefined = queryParam[filterKey];
						if (parameter == undefined)
							return;

						const decodedParam: string = decodeURIComponent(parameter);
						this._prepareFilterControls(decodedParam, cohort, filterKey);
					}
					else {
						this._prepareSubOptionFilterControls(cohort, queryParam);
					}
				});
			});

		subscription.unsubscribe();
	}
}
